import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Theme Context
const ThemeContext = createContext();

// Theme Actions
const THEME_ACTIONS = {
  SET_THEME: 'SET_THEME',
  TOGGLE_THEME: 'TOGGLE_THEME',
  SET_SIDEBAR_COLLAPSED: 'SET_SIDEBAR_COLLAPSED',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  SET_COMPACT_MODE: 'SET_COMPACT_MODE',
  TOGGLE_COMPACT_MODE: 'TOGGLE_COMPACT_MODE',
};

// Initial State
const initialState = {
  theme: localStorage.getItem('theme') || 'light',
  sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
  compactMode: localStorage.getItem('compactMode') === 'true',
};

// Theme Reducer
function themeReducer(state, action) {
  switch (action.type) {
    case THEME_ACTIONS.SET_THEME:
      return {
        ...state,
        theme: action.payload,
      };

    case THEME_ACTIONS.TOGGLE_THEME:
      return {
        ...state,
        theme: state.theme === 'light' ? 'dark' : 'light',
      };

    case THEME_ACTIONS.SET_SIDEBAR_COLLAPSED:
      return {
        ...state,
        sidebarCollapsed: action.payload,
      };

    case THEME_ACTIONS.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed,
      };

    case THEME_ACTIONS.SET_COMPACT_MODE:
      return {
        ...state,
        compactMode: action.payload,
      };

    case THEME_ACTIONS.TOGGLE_COMPACT_MODE:
      return {
        ...state,
        compactMode: !state.compactMode,
      };

    default:
      return state;
  }
}

// Theme Provider Component
export function ThemeProvider({ children }) {
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Persist theme changes to localStorage
  useEffect(() => {
    localStorage.setItem('theme', state.theme);
    
    // Apply theme to document
    if (state.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.theme]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', state.sidebarCollapsed.toString());
  }, [state.sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem('compactMode', state.compactMode.toString());
  }, [state.compactMode]);

  // Set theme
  const setTheme = (theme) => {
    dispatch({
      type: THEME_ACTIONS.SET_THEME,
      payload: theme,
    });
  };

  // Toggle theme
  const toggleTheme = () => {
    dispatch({ type: THEME_ACTIONS.TOGGLE_THEME });
  };

  // Set sidebar collapsed state
  const setSidebarCollapsed = (collapsed) => {
    dispatch({
      type: THEME_ACTIONS.SET_SIDEBAR_COLLAPSED,
      payload: collapsed,
    });
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    dispatch({ type: THEME_ACTIONS.TOGGLE_SIDEBAR });
  };

  // Set compact mode
  const setCompactMode = (compact) => {
    dispatch({
      type: THEME_ACTIONS.SET_COMPACT_MODE,
      payload: compact,
    });
  };

  // Toggle compact mode
  const toggleCompactMode = () => {
    dispatch({ type: THEME_ACTIONS.TOGGLE_COMPACT_MODE });
  };

  const value = {
    // State
    theme: state.theme,
    sidebarCollapsed: state.sidebarCollapsed,
    compactMode: state.compactMode,

    // Actions
    setTheme,
    toggleTheme,
    setSidebarCollapsed,
    toggleSidebar,
    setCompactMode,
    toggleCompactMode,

    // Computed values
    isDark: state.theme === 'dark',
    isLight: state.theme === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}
